import { useState } from 'react'
import { useAuth } from '../../../context/AuthContext'
import { useBranding } from '../../../context/BrandingContext'
import { authService } from '../../../services/authService'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Lock, Loader2, CheckCircle, XCircle, ArrowLeft, ShieldCheck, KeyRound } from 'lucide-react'

function Field({ label, value }: { label: string; value?: string | null }) {
    const displayValue = value || '—'
    const isEmpty = !value

    return (
        <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1.5">
                {label}
            </label>
            <input
                readOnly
                value={displayValue}
                className={`w-full px-4 py-3 rounded-2xl border border-slate-200 bg-slate-50 text-sm font-semibold outline-none cursor-default ${isEmpty ? 'text-slate-400' : 'text-slate-700'}`}
            />
        </div>
    )
}

export default function MyAccountTab() {
    const { user } = useAuth()
    const { branding } = useBranding()
    const hiddenFields: string[] = branding?.branding?.hidden_fields ?? []
    const showFatherName = user?.is_applicant && !hiddenFields.includes('father_name')
    const showMotherName = user?.is_applicant && !hiddenFields.includes('mother_name')
    const showFamilySection = showFatherName || showMotherName
    const [showPasswordForm, setShowPasswordForm] = useState(false)
    const [currentPassword, setCurrentPassword] = useState('')
    const [newPassword, setNewPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [success, setSuccess] = useState(false)

    const fullName = user
        ? [user.first_name, user.last_name].filter(Boolean).join(' ') || '—'
        : '—'

    const handlePasswordSubmit = async (event: React.FormEvent) => {
        event.preventDefault()
        setError(null)

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match.')
            return
        }

        if (newPassword.length < 8) {
            setError('Password must be at least 8 characters.')
            return
        }

        setIsSubmitting(true)
        try {
            await authService.changePassword(currentPassword, newPassword, confirmPassword)
            setSuccess(true)
            setCurrentPassword('')
            setNewPassword('')
            setConfirmPassword('')
            setTimeout(() => {
                setShowPasswordForm(false)
                setSuccess(false)
            }, 2000)
        } catch (apiError: any) {
            const detail = apiError.response?.data?.detail || 
                           apiError.response?.data?.current_password?.[0] ||
                           apiError.response?.data?.new_password?.[0] ||
                           apiError.response?.data?.confirm_password?.[0] ||
                           'Failed to change password. Please try again.'
            setError(Array.isArray(detail) ? detail[0] : detail)
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleCancel = () => {
        setShowPasswordForm(false)
        setCurrentPassword('')
        setNewPassword('')
        setConfirmPassword('')
        setError(null)
        setSuccess(false)
    }

    // Password change form view
    if (showPasswordForm) {
        return (
            <div className="w-full min-h-[50vh] flex items-center justify-center px-4 pb-10">
                <div className="w-full max-w-sm">
                    {success ? (
                        <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 shadow-sm text-center">
                            <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-green-500 flex items-center justify-center">
                                <CheckCircle className="w-8 h-8 text-white" />
                            </div>
                            <h2 className="text-xl font-bold text-slate-800 mb-2">Password Changed!</h2>
                            <p className="text-sm text-slate-500">Your password has been updated successfully.</p>
                        </div>
                    ) : (
                        <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 shadow-sm">
                            <button 
                                onClick={handleCancel}
                                className="mb-5 flex min-h-[44px] min-w-[44px] items-center gap-2 px-3 py-2 text-sm text-slate-500 active:text-slate-700"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                Back
                            </button>

                            <div className="text-center mb-6">
                                <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-[#001B44] flex items-center justify-center">
                                    <KeyRound className="w-7 h-7 text-white" />
                                </div>
                                <h2 className="text-lg font-bold text-slate-800">Change Password</h2>
                                <p className="text-sm text-slate-500 mt-1">Enter your new password</p>
                            </div>

                            {error && (
                                <div className="flex items-start gap-3 p-3 mb-5 bg-red-50 text-red-700 rounded-xl border border-red-100">
                                    <XCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                                    <span className="text-sm">{error}</span>
                                </div>
                            )}

                            <form onSubmit={handlePasswordSubmit} className="space-y-4">
                                <div>
                                    <label htmlFor="current-password" className="block text-xs font-semibold text-slate-600 mb-1.5">
                                        Current Password
                                    </label>
                                    <div className="relative">
                                        <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                        <Input
                                            id="current-password"
                                            type="password"
                                            value={currentPassword}
                                            onChange={(event) => setCurrentPassword(event.target.value)}
                                            placeholder="Current password"
                                            className="w-full pl-11 pr-4 py-3.5 h-auto rounded-xl border border-slate-200 bg-slate-50 text-sm placeholder:text-slate-500 placeholder:text-sm focus:border-[#001B44] focus:bg-white focus:ring-2 focus:ring-[#001B44]/10"
                                            required
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="new-password" className="block text-xs font-semibold text-slate-600 mb-1.5">
                                        New Password
                                    </label>
                                    <div className="relative">
                                        <KeyRound className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                        <Input
                                            id="new-password"
                                            type="password"
                                            value={newPassword}
                                            onChange={(event) => setNewPassword(event.target.value)}
                                            placeholder="New password"
                                            className="w-full pl-11 pr-4 py-3.5 h-auto rounded-xl border border-slate-200 bg-slate-50 text-sm placeholder:text-slate-500 placeholder:text-sm focus:border-[#001B44] focus:bg-white focus:ring-2 focus:ring-[#001B44]/10"
                                            required
                                            minLength={8}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="confirm-password" className="block text-xs font-semibold text-slate-600 mb-1.5">
                                        Confirm Password
                                    </label>
                                    <div className="relative">
                                        <ShieldCheck className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                        <Input
                                            id="confirm-password"
                                            type="password"
                                            value={confirmPassword}
                                            onChange={(event) => setConfirmPassword(event.target.value)}
                                            placeholder="Confirm password"
                                            className="w-full pl-11 pr-4 py-3.5 h-auto rounded-xl border border-slate-200 bg-slate-50 text-sm placeholder:text-slate-500 placeholder:text-sm focus:border-[#001B44] focus:bg-white focus:ring-2 focus:ring-[#001B44]/10"
                                            required
                                            minLength={8}
                                        />
                                    </div>
                                </div>

                                <div className="flex flex-col sm:flex-row gap-3 pt-3">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={handleCancel}
                                        className="w-full sm:flex-1 h-12 rounded-xl font-semibold border-slate-200 active:bg-slate-100"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="w-full sm:flex-1 h-12 rounded-xl font-semibold bg-[#001B44] active:bg-[#001B44]/90 disabled:opacity-50"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                                Saving...
                                            </>
                                        ) : (
                                            'Update Password'
                                        )}
                                    </Button>
                                </div>

                                <p className="text-xs text-center text-slate-400 pt-1">
                                    Min 8 characters with at least one number
                                </p>
                            </form>
                        </div>
                    )}
                </div>
            </div>
        )
    }

    return (
        <div className="w-full space-y-6 pb-10">

            {/* Account Section */}
            <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-sm">
                <div className="mb-6">
                    <h3 className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 mb-2">Account</h3>
                    <div className="h-1 w-10 bg-[#001B44] rounded-full" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field label="Full Name" value={fullName} />
                    <Field label="Email Address" value={user?.email} />
                </div>
                <div className="mt-6 pt-6 border-t border-slate-100">
                    <Button
                        onClick={() => setShowPasswordForm(true)}
                        variant="outline"
                        className="h-11 px-6 rounded-2xl font-semibold border-slate-200 hover:bg-slate-50"
                    >
                        <Lock className="w-4 h-4 mr-2" />
                        Reset Password
                    </Button>
                </div>
            </div>

            {/* Personal Information Section */}
            <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-sm">
                <div className="mb-6">
                    <h3 className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 mb-2">Personal Information</h3>
                    <div className="h-1 w-10 bg-[#001B44] rounded-full" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field label="Citizenship" value={user?.citizenship} />
                    <Field label="Secondary Citizenship" value={user?.secondary_citizenship} />
                    <Field label="Country of Birth" value={user?.country_of_birth} />
                    <Field label="City of Birth" value={user?.city_of_birth} />
                    <Field label="Preferred Name" value={user?.preferred_name} />
                </div>
            </div>

            {showFamilySection && (
            <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-sm">
                <div className="mb-6">
                    <h3 className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 mb-2">Family Information</h3>
                    <div className="h-1 w-10 bg-[#001B44] rounded-full" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {showFatherName && <Field label="Father's Name" value={user?.father_name} />}
                    {showMotherName && <Field label="Mother's Name" value={user?.mother_name} />}
                </div>
            </div>
            )}
            <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-sm">
                <div className="mb-6">
                    <h3 className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 mb-2">Address Information</h3>
                    <div className="h-1 w-10 bg-[#001B44] rounded-full" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field label="Address: Country" value={user?.address_country} />
                    <Field label="Address: Street" value={user?.address_street} />
                    <Field label="Address: City" value={user?.address_city} />
                    <Field label="Address: Zip Code" value={user?.address_zip_code} />
                </div>
            </div>

        </div>
    )
}
